import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from 'react';
import styled from 'astroturf/react';
import { currentUser } from 'js/utils/user';
import { TextButton } from 'js/design_system/TextButton';
import Divider from 'js/design_system/Divider';
import { FeedContext } from './contexts/feed';
import Feed from './Feed/Feed';
import useFeedConfig from './Feed/hooks/useFeedConfig';
import useNews from './Feed/hooks/useNews';
import useUsers from './Feed/hooks/useUsers';
import { getProfileFeedPage } from './api/feed_api';
import { PostPrompt as PostPromptBase } from './PostPrompt';
import ProfileEmptyState from './ProfileEmptyState';
import CollapsedActivity from './CollapsedActivity';
import { NewsPreview } from './shared/types';
import { ReactQueryProvider } from 'js/social/state/ReactQueryProvider';
import { ExploreConversations } from 'js/social/ExploreConversations';
import { FeedFilters } from 'js/social/FeedFilters';
import BackButton from 'js/social/shared/BackButton';
import { SnackbarProvider } from 'js/social/state/SnackbarProvider';
import { ProfileBulkFollowModal } from 'js/social/shared/big_cards/ProfileBulkFollowModal';

// Lazy load InterruptionChooser to avoid SSR issues with i18next initialization
const InterruptionChooser = lazy(() => import('js/liswp/interruption_chooser'));

const PAGE = 'profile';

const Container = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: -16px;

  @include medium-up {
    margin: 0;
  }
`;

const PostPromptExpanded = styled(PostPromptBase)``;

const PostPromptCollapsed = styled(PostPromptBase)`
  padding: 0;
  border: none;
`;

const ExpandedActivity = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  width: 100%;
  padding: 0 0 16px 0;

  @include medium-up {
    padding: 0 16px 16px 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
`;

const ExpandCtaButton = styled(TextButton)`
  height: auto;
  padding: 4px 10px;
`;

type Props = {
  currentUserId: number;
  viewedUserId: number;
  viewedUserDisplayName: string;
  hasActivity: boolean;
  preview?: NewsPreview;
  filters: string[];
  onsitePodcastPromotion?: boolean;
};

export const ProfileFeed: React.FC<Props> = ({
  currentUserId,
  viewedUserId,
  viewedUserDisplayName,
  hasActivity,
  preview,
  filters,
  onsitePodcastPromotion = false,
}) => {
  const [filter, setFilter] = useState<string>();
  const [showFeed, setShowFeed] = useState(false);

  const getPage = useMemo(
    () => getProfileFeedPage(viewedUserId, filter),
    [viewedUserId, filter],
  );

  const feedConfig = useFeedConfig(PAGE);
  const userActions = useUsers(preview?.users);
  const newsActions = useNews({
    page: feedConfig.page,
    getPage,
    setMoreUsers: userActions.setMoreUsers,
    setFeedConfig: feedConfig.setFeedConfig,
  });
  const worksContainerRef = useRef<HTMLElement | null>(null);
  const isOwner = viewedUserId === currentUserId;

  useEffect(() => {
    // The element with 'social-redesign-work-container' id is implemented
    // in backbone, and is very old.
    // The way this component interacts with it is by showing/hiding it
    // when we want to hide/show the profile feed
    worksContainerRef.current = document.getElementById(
      'social-redesign-work-container',
    );
  }, []);

  useEffect(() => {
    if (!worksContainerRef.current) return;
    worksContainerRef.current.style.display = showFeed ? 'none' : '';
  }, [showFeed]);

  return (
    <ReactQueryProvider>
      <SnackbarProvider>
        <FeedContext.Provider
          value={{
            feedConfig,
            userActions,
            newsActions,
            useReporting: true,
            recordViews: false,
          }}
        >
          <Container>
            <ProfileBulkFollowModal
              viewedUserDisplayName={viewedUserDisplayName}
              userId={viewedUserId}
            />

            {/* Paper claiming modal for own profile (variant_b of home/profile test) */}
            {/* Only render on client-side to avoid SSR issues with i18next */}
            {isOwner && typeof window !== 'undefined' && (
              <Suspense fallback={null}>
                <InterruptionChooser
                  premium={currentUser?.premium ?? false}
                  pageType="profile"
                  viewedUserId={viewedUserId}
                  forceRender={null}
                  showTranslationModal={false}
                  considerUserSentimentPopup={false}
                />
              </Suspense>
            )}

            {showFeed ? (
              <>
                <div>
                  <BackButton onClick={() => setShowFeed(false)}>
                    Back
                  </BackButton>
                  <FeedFilters
                    filter={filter}
                    filters={filters}
                    onFilterChange={setFilter}
                  />
                </div>

                <ExpandedActivity>
                  {isOwner && currentUser && (
                    <PostPromptExpanded
                      fullName={currentUser.display_name || ''}
                      photoUrl={currentUser.photo.replace('s65', 's200')}
                      profileUrl={currentUser.url || ''}
                    />
                  )}

                  <Feed
                    filter={filter}
                    emptyState={
                      <ProfileEmptyState
                        isOwner={isOwner}
                        username={viewedUserDisplayName}
                      />
                    }
                    showReasons={filter === undefined}
                    {...newsActions}
                    {...userActions}
                  />
                </ExpandedActivity>
              </>
            ) : hasActivity || isOwner ? (
              <>
                <CollapsedActivity
                  isOwner={isOwner}
                  displayName={viewedUserDisplayName}
                  preview={preview}
                  onsitePodcastPromotion={onsitePodcastPromotion}
                >
                  {isOwner && currentUser && (
                    <>
                      <PostPromptCollapsed
                        fullName={currentUser.display_name || ''}
                        photoUrl={currentUser.photo.replace('s65', 's200')}
                        profileUrl={currentUser.url || ''}
                      />
                      {preview && <Divider.s8 />}
                    </>
                  )}
                </CollapsedActivity>

                {hasActivity && (
                  <ButtonContainer>
                    <ExpandCtaButton inline onClick={() => setShowFeed(true)}>
                      Show all activity
                    </ExpandCtaButton>
                  </ButtonContainer>
                )}
              </>
            ) : (
              <ExploreConversations displayName={viewedUserDisplayName} />
            )}
          </Container>
        </FeedContext.Provider>
      </SnackbarProvider>
    </ReactQueryProvider>
  );
};
